import React from "react";
import {useLocation, useNavigate} from "react-router-dom";
import Icon from "../../system/components/icon";
import {formatPersianDate} from "../../models/goal-update/goal-update-api";

// The fixed template of a goal / project update sent into a chat (boss's task «تغییر رفتار مودال نمایش بروزرسانی پروژه
// و هدف»; docs/tasks/23-update-share-to-chat.md). The card data is a snapshot made by the server when it was sent; a click
// opens the update's view modal on the current page (?goalUpdate= / ?projectUpdate=, handled on every route).

const STATE_TONES = {
    "on-way": {color: "#0f766e", background: "#ccfbf1"},
    "has-risks": {color: "#b45309", background: "#fef3c7"},
    "out-of-way": {color: "#b91c1c", background: "#fee2e2"},
    achieved: {color: "#047857", background: "#d1fae5"},
    canceled: {color: "#57534e", background: "#f5f5f4"},
    archived: {color: "#57534e", background: "#f5f5f4"},
};

export const isUpdateCard = (reference) => ["goal-update", "project-update"].includes(reference?.type);

export function updateCardTarget(reference, location) {
    const param = reference?.type === "project-update" ? "projectUpdate" : "goalUpdate";
    const params = new URLSearchParams(location?.search || "");
    params.set(param, reference?.documentId);
    return `${location?.pathname || "/"}?${params.toString()}`;
}

export function UpdateCardView({reference, onClick, preview = false}) {
    const project = reference?.type === "project-update";
    const tone = STATE_TONES[reference?.state] || STATE_TONES.canceled;
    const progress = Number.isFinite(Number(reference?.progress)) && reference?.progress !== null ? Math.max(0, Math.min(100, Number(reference.progress))) : null;
    const clickable = Boolean(onClick);
    return (
        <div
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={onClick}
            onKeyDown={(event) => {
                if (clickable && ["Enter", " "].includes(event.key)) {
                    event.preventDefault();
                    onClick();
                }
            }}
            className={`update-chat-card w-full surface-card border-1 border-round-xl overflow-hidden ${clickable ? "cursor-pointer hover:surface-50" : ""}`}
            style={{borderColor: "#fde0d6", maxWidth: preview ? "22rem" : undefined}}
            dir="rtl"
        >
            <div className="flex align-items-center gap-2 px-3 py-2" style={{background: "#fdf3f0"}}>
                <Icon icon={`mi mi:${project ? "campaign" : "track_changes"}`} className="text-lg" style={{color: "#e6531e"}}/>
                <span className="text-xs font-bold" style={{color: "#e6531e"}}>{project ? "بروزرسانی پروژه" : "بروزرسانی هدف"}</span>
                {reference?.publishedAt && <span className="text-2xs text-500 mr-auto">{formatPersianDate(reference.publishedAt)}</span>}
            </div>
            <div className="p-3 flex flex-column gap-2">
                <strong className="text-sm line-height-3">{reference?.title || "بدون عنوان"}</strong>
                <div className="flex flex-wrap align-items-center gap-2">
                    {reference?.stateLabel && (
                        <span className="text-2xs font-bold px-2 py-1 border-round-2xl" style={{color: tone.color, background: tone.background}}>{reference.stateLabel}</span>
                    )}
                    {progress !== null && <span className="text-2xs text-600">{`پیشرفت ${progress.toLocaleString("fa-IR")}٪`}</span>}
                </div>
                {progress !== null && (
                    <div className="border-round-2xl overflow-hidden" style={{height: "0.35rem", background: "#f5f5f4"}}>
                        <div style={{width: `${progress}%`, height: "100%", background: "#e6531e"}}/>
                    </div>
                )}
                {reference?.summary && <span className="text-xs text-700 line-height-3">{reference.summary}</span>}
                <div className="flex align-items-center justify-content-between gap-2 pt-1">
                    <span className="text-2xs text-500">{reference?.author ? `ثبت‌کننده: ${reference.author}` : ""}</span>
                    <span className="text-2xs font-bold flex align-items-center gap-1" style={{color: "#e6531e"}}>
                        مشاهده گزارش<Icon icon="mi mi:chevron_left" className="text-sm"/>
                    </span>
                </div>
            </div>
        </div>
    );
}

// in a chat: a click opens the update on the page the chat is on
export default function UpdateChatCard({reference}) {
    const navigate = useNavigate();
    const location = useLocation();
    return <UpdateCardView reference={reference} onClick={() => navigate(updateCardTarget(reference, location))}/>;
}
